import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { unitService } from "../services/unit.service";

export const fetchUnits = createAsyncThunk("unit/fetchAll", async (_, thunkAPI) => {
  try {
    return await unitService.getUnits();
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch units");
  }
});

export const fetchActiveUnits = createAsyncThunk("unit/fetchActive", async (_, thunkAPI) => {
  try {
    return await unitService.getActiveUnits();
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch active units");
  }
});

export const fetchUnitById = createAsyncThunk("unit/fetchById", async (id, thunkAPI) => {
  try {
    return await unitService.getUnitById(id);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch unit");
  }
});

export const addUnit = createAsyncThunk("unit/add", async (unit, thunkAPI) => {
  try {
    return await unitService.createUnit(unit);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to create unit");
  }
});

export const editUnit = createAsyncThunk("unit/edit", async ({ id, unit }, thunkAPI) => {
  try {
    return await unitService.updateUnit(id, unit);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to update unit");
  }
});

export const removeUnit = createAsyncThunk("unit/remove", async (id, thunkAPI) => {
  try {
    await unitService.deleteUnit(id);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to delete unit");
  }
});

const initialState = {
  units: [],
  totalCount: 0,
  loading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
};

const unitSlice = createSlice({
  name: "unit",
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch units
      .addCase(fetchUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.units = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch active units
      .addCase(fetchActiveUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.units = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      })
      .addCase(fetchActiveUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add unit
      .addCase(addUnit.pending, (state) => {
        state.loading = true;
      })
      .addCase(addUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units.push(action.payload);
      })
      .addCase(addUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Edit unit
      .addCase(editUnit.pending, (state) => {
        state.loading = true;
      })
      .addCase(editUnit.fulfilled, (state, action) => {
        state.loading = false;
        // Optimistic update: use the data from meta.arg since backend returns empty response
        const { id, unit } = action.meta.arg;
        state.units = state.units.map((u) => {
          if (u.id === id) return { ...u, ...unit };
          return u;
        });
        state.showModal = false;
      })
      .addCase(editUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Remove unit
      .addCase(removeUnit.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units = state.units.filter((unit) => unit.id !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(removeUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { openModal, closeModal, openDeleteModal, closeDeleteModal, clearError } = unitSlice.actions;

export default unitSlice.reducer;